/* src/components/heroManagementDashboard/aiWorkplace/AIWorkplaceLogin.js */
import React, { useState, startTransition } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "../../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { setHeroUser } from "../../../redux/actions";
import LoadingCircleSmall from "../../utils/LoadingCircleSmall";
import tpcAndUSLogo from "../../../images/AndUsLoginLogo.png";
import "./AIWorkplaceLogin.css";

export default function AIWorkplaceLogin() {
  /* form state */
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);

  const nav = useNavigate();
  const dispatch = useDispatch();

  const seedAIWorkplaceUserFromUserRecords = async (email) => {
    const fn = getFunctions();
    const readDocSafe = httpsCallable(fn, "firestoreReadDocSafe");
    const primaryCompany = "TPC_Group";
    const collectionBrand = "TechPerformanceCentre";

    const tryPath = async (companyKey, brandKey) => {
      const res = await readDocSafe({
        company: companyKey,
        collectionBrand: brandKey,
        systemName: "AIWorkplaceUsers",
        uniqueId: email,
      });
      return res.data; // { found, data? }
    };

    // Try exact brand, then shared
    let result = await tryPath(primaryCompany, collectionBrand);
    if (!result.found) result = await tryPath(primaryCompany, "shared");
    const userDoc = result.found ? result.data : {};

    // Get name from userValidation
    let userName = "";
    try {
      const valRes = await readDocSafe({
        company: "TPC_Group",
        collectionBrand: "TechPerformanceCentre",
        systemName: "userValidation",
        uniqueId: email,
      });
      const doc = valRes?.data;
      userName = doc?.name || "";
    } catch {}

    // Optional: fetch group details
    let userGroupDetails = {};
    let userGroupPermissions = [];
    let userGroupSettings = {};
    if (userDoc?.userGroup && userDoc.userGroup !== "Public") {
      try {
        const groupRes = await readDocSafe({
          company: primaryCompany,
          collectionBrand,
          systemName: "UserGroups",
          uniqueId: userDoc.userGroup,
        });
        if (groupRes.data.found) {
          const groupData = groupRes.data.data;
          userGroupDetails = {
            groupName: groupData.groupName,
            hierarchy: groupData.hierarchy,
            roles: groupData.roles || [],
            permissions: groupData.permissions || [],
            settings: groupData.settings || {},
          };
          userGroupPermissions = groupData.permissions || [];
          userGroupSettings = groupData.settings || {};
        }
      } catch {}
    }

    const payload = {
      uid: auth.currentUser?.uid || "",
      email,
      name: userName || userDoc?.name || userDoc?.fullName || "",
      company: primaryCompany,
      collectionBrand,
      userGroup: userDoc.userGroup || "",
      userRole: userDoc.role || "AI Workplace User",
      userGroupDetails,
      userGroupPermissions,
      userGroupSettings,
    };

    console.log("[AIW] setHeroUser values:", {
      uid: payload.uid,
      email: payload.email,
      name: payload.name,
      company: payload.company,
      collectionBrand: payload.collectionBrand,
      userGroup: payload.userGroup,
      userRole: payload.userRole,
    });

    dispatch(setHeroUser(payload));
  };

  /* ───────────── login handler ───────────── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setBusy(true);

    try {
      await signInWithEmailAndPassword(auth, email, pw);

      if (!auth.currentUser.emailVerified)
        throw new Error("Please verify your e-mail first.");

      // Check if user has completed profile
      const fn = getFunctions();
      const readDocSafe = httpsCallable(fn, "firestoreReadDocSafe");
      const userResult = await readDocSafe({
        company: "TPC_Group",
        collectionBrand: "TechPerformanceCentre",
        systemName: "AIWorkplaceUsers",
        uniqueId: email,
      });

      // seed Redux with same hero-user structure
      await seedAIWorkplaceUserFromUserRecords(email);

      // Check if profile is complete
      const isComplete =
        userResult.data?.found && userResult.data?.data?.isCompleted === true;

      console.log("[AIW] Login successful, isComplete:", isComplete);

      if (isComplete) {
        console.log("[AIW] Profile complete, redirecting to dashboard");
        startTransition(() => nav("/ai-workplace-dashboard"));
      } else {
        console.log("[AIW] Profile incomplete, redirecting to welcome");
        startTransition(() => nav("/ai-workplace-welcome"));
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  /* ───────────── forgot password handler ───────────── */
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");
    setBusy(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess("Password reset email sent. Please check your inbox.");
    } catch (err) {
      setErr(err.message);
    } finally {
      setBusy(false);
    }
  };

  /* ───────────── toggle forgot password ───────────── */
  const toggleForgotPassword = () => {
    setErr("");
    setSuccess("");
    setForgotPassword((prev) => !prev);
  };

  /* ───────────── Google sign-in handler ───────────── */
  const handleGoogleSignIn = async () => {
    setErr("");
    setSuccess("");
    setBusy(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      console.log("[AIW] Google sign-in successful:", user.email);

      // Check if user has completed profile
      const fn = getFunctions();
      const readDocSafe = httpsCallable(fn, "firestoreReadDocSafe");
      const userResult = await readDocSafe({
        company: "TPC_Group",
        collectionBrand: "TechPerformanceCentre",
        systemName: "AIWorkplaceUsers",
        uniqueId: user.email,
      });

      // Seed Redux with hero-user structure from Firestore
      await seedAIWorkplaceUserFromUserRecords(user.email);

      // Check if profile is complete
      const isComplete =
        userResult.data?.found && userResult.data?.data?.isCompleted === true;

      console.log("[AIW] Google login successful, isComplete:", isComplete);

      if (isComplete) {
        console.log("[AIW] Profile complete, redirecting to dashboard");
        startTransition(() => nav("/ai-workplace-dashboard"));
      } else {
        console.log("[AIW] Profile incomplete, redirecting to welcome");
        startTransition(() => nav("/ai-workplace-welcome"));
      }
    } catch (e) {
      console.error("[AIW] Google sign-in error:", e);
      if (e.code === "auth/popup-closed-by-user") {
        setErr("Sign-in cancelled. Please try again.");
      } else if (e.code === "auth/popup-blocked") {
        setErr("Pop-up blocked. Please enable pop-ups and try again.");
      } else {
        setErr(e.message || "Google sign-in failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  /* ───────────── render ───────────── */
  if (busy)
    return (
      <LoadingCircleSmall
        label={forgotPassword ? "Sending reset email…" : "Logging in…"}
      />
    );

  return (
    <div className="aiwl-container">
      {/* banner */}
      <div className="aiwl-left">
        <div className="aiwl-logo-wrap">
          <img src={tpcAndUSLogo} alt="TPC" className="aiwl-logo" />
        </div>

        <div className="aiwl-welcome-wrap">
          <div className="aiwl-welcome-text">Welcome Back</div>
        </div>
      </div>

      {/* form */}
      <div className="aiwl-right">
        <form
          className="aiwl-form"
          onSubmit={forgotPassword ? handleForgotPassword : handleLogin}
        >
          <h2 className="aiwl-title">
            {forgotPassword ? "Reset Password" : "AI Workplace Login"}
          </h2>
          {err && <p className="aiwl-error">{err}</p>}
          {success && <p className="aiwl-success">{success}</p>}
          <input
            type="email"
            placeholder="Email"
            className="aiwl-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {!forgotPassword && (
            <input
              type="password"
              placeholder="Password"
              className="aiwl-input"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
            />
          )}
          <button className="aiwl-button" type="submit">
            {forgotPassword ? "Send Reset Email" : "Login"}
          </button>

          {!forgotPassword && (
            <>
              <div className="aiwl-divider">
                <span>or</span>
              </div>

              <button
                type="button"
                className="aiwl-button aiwl-google-button"
                onClick={handleGoogleSignIn}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9.003 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </button>
            </>
          )}

          <div className="aiwl-forgot-password">
            <button
              type="button"
              className="aiwl-forgot-toggle"
              onClick={toggleForgotPassword}
            >
              {forgotPassword ? "Back to Login" : "Forgot Password?"}
            </button>
          </div>
        </form>
        <div className="aiwl-register-link">
          Not registered? <Link to="/ai-workplace-register">Sign up here</Link>
        </div>
      </div>
    </div>
  );
}
